/**
 * Previous/Next navigation and the counter's grammar — brief §13.
 *
 * Pure functions over cards and sides, in their own module for the same reason
 * deckFocusPositioning.ts is: the rules here are arithmetic and identity
 * matching, and they are worth asserting directly rather than through a
 * rendered feed.
 *
 * The load-bearing rule is field-TYPE preservation. Walking from card 42 to 43
 * to 44 with the caret staying in `Translation` is what the brief calls "mass
 * editing without a mass-editing mode", and it is the whole point of the
 * arrows. Where a card repeats a type, the caret lands on the TOPMOST field of
 * it — ADR-066 Decision 1: the first field of a repeating side-B kind is the
 * one authoritative for Learn, so it is the one an author correcting a deck
 * means. Landing on a later duplicate would let someone fix every card in the
 * deck and change nothing about what is studied.
 */
import type { Card, CardSide } from '../types'
import { customSideName, isTermSide } from './cardSides'
import { TERM_FIELD_KEY } from '../components/shared/VocabCard'

/**
 * What "the same kind of field" means when moving between cards.
 *
 * `kind` is machine identity (ADR-040). `customName` matters for exactly one
 * kind: `note`, whose label is authored rather than fixed (`isCustomNamedSide`
 * — every note-kind side is a Custom field; there is no other reading of that
 * kind any more). Two custom fields named "Etymology" and "Mnemonic" are both
 * kind `note` and are emphatically not the same field to their author, so the
 * name is part of the type — otherwise a walk holding "Etymology" would land
 * in "Mnemonic". `customName` is `null` only for a note-kind side with no
 * label at all, which stored data never has (the backend requires a non-empty
 * label); it exists as a type-level fallback, not a case this file expects to
 * hit.
 */
export interface FieldType {
  kind: string
  customName: string | null
}

/** The Term, as a type. Term is not a side-B field, but it is a thing the caret
 * can be in and therefore a thing a walk can hold — and it is the one type
 * every card is guaranteed to have, which makes it the safe fallback. */
export const TERM_FIELD_TYPE: FieldType = { kind: 'term', customName: null }

export function sameFieldType(a: FieldType, b: FieldType): boolean {
  if (a.kind !== b.kind) return false
  // Only `note` distinguishes by name; for every other kind the label is fixed
  // and comparing it would split one type in two on a stale stored label.
  if (a.kind !== 'note') return true
  return a.customName === b.customName
}

/** The type of a stored side. */
export function fieldTypeOfSide(side: CardSide): FieldType {
  const kind = side.kind ?? 'note'
  if (kind === 'term') return TERM_FIELD_TYPE
  return { kind, customName: kind === 'note' ? (customSideName(side) || null) : null }
}

/** The type of a field as the active card's own draft holds it — the shape
 * ActiveVocabCard reports on focus. Used rather than the stored side because a
 * field added this session via `+ Field` has no stored side yet, and a walk
 * started from one must still hold its type. */
export function fieldTypeOfDraft(draft: { kind: string; label: string; isCustom: boolean }): FieldType {
  if (draft.kind === 'term') return TERM_FIELD_TYPE
  return {
    kind: draft.kind,
    customName: draft.kind === 'note' && draft.isCustom ? (draft.label.trim() || null) : null,
  }
}

/** Side-B sides in stored position order. The API already returns them ordered,
 * but "topmost" is a positional claim and sorting makes it one rather than a
 * bet on the transport. */
function orderedSideBSides(card: Card): CardSide[] {
  return [...card.sides].filter((s) => !isTermSide(s)).sort((a, b) => a.position - b.position)
}

/**
 * The field key a walk holding `type` should land on in `card`, or null when
 * the card has no field of that type — the case brief §13 answers with an
 * `+ Add Example` affordance rather than by silently landing somewhere else.
 *
 * Returns the TOPMOST match (ADR-066), never a later duplicate.
 */
export function findFieldKeyOfType(card: Card, type: FieldType): string | null {
  if (type.kind === 'term') return TERM_FIELD_KEY
  const match = orderedSideBSides(card).find((s) => sameFieldType(fieldTypeOfSide(s), type))
  return match ? match.id : null
}

/**
 * Which card a press of an arrow moves to, as an index into the SHOWN set —
 * or null when the press does nothing. Brief §13: "the arrows walk exactly the
 * shown set and never jump to a card that is not in the list", so every answer
 * here is an index into that set and the caller passes nothing else.
 *
 * `activeIndex` is null when no card is active (or when the active thing is the
 * unsaved new card, which has no place in the shown set). Brief §4.2 decides
 * that case: the arrows stay live and a press activates the FIRST card.
 */
export function stepTarget(
  activeIndex: number | null,
  direction: -1 | 1,
  shownCount: number,
): number | null {
  if (shownCount <= 0) return null
  if (activeIndex === null) return 0
  const next = activeIndex + direction
  return next >= 0 && next < shownCount ? next : null
}

/** Whether an arrow is live, so the bar can render it inert at the ends without
 * duplicating the rule above. */
export function canStep(
  activeIndex: number | null,
  direction: -1 | 1,
  shownCount: number,
): boolean {
  return stepTarget(activeIndex, direction, shownCount) !== null
}

/**
 * Brief §13's three counter states. They are three GRAMMARS, not three strings:
 * the numbers and the frame around them change together.
 *
 *   plain  `42 OF 150`        grey caption, deck's true size
 *   shown  `7 OF 24 SHOWN`    funnel, accent backing — a filter really did narrow the deck
 *   found  `2 OF 5 FOUND`     magnifier, exit ×, no backing — search narrowed only the walk
 *
 * Search wins over filter when both are on: the reader typed the query most
 * recently and it is the narrower claim of the two.
 */
export type CounterGrammar = 'plain' | 'shown' | 'found'

export function counterGrammar(state: { searching: boolean; filtered: boolean }): CounterGrammar {
  if (state.searching) return 'found'
  if (state.filtered) return 'shown'
  return 'plain'
}

/**
 * The counter's denominator.
 *
 * The plain grammar counts the DECK, not the pages loaded so far. A counter fed
 * by the loaded set is the defect recorded in brief §23 item 9 and ADR-067
 * Decision 6 — a 93-card deck reading `30` until scrolled — and `deckCardCount`
 * comes from the deck record, so it is right before anything has been scrolled.
 *
 * `FOUND` now counts the DECK's matches too. This is the arrival implementation
 * item 5 was reserved for: search runs on the server, so the match count is a
 * server count over the whole deck rather than over the loaded pages. Passing
 * `foundTotal` is therefore how a caller says "this number is trustworthy"; the
 * fallback to `shownCount` covers only the moment before the first response,
 * where the shown set genuinely is everything known.
 *
 * `SHOWN` still counts the shown set, and that is not the same compromise: the
 * filter chips are a client-side narrowing of the loaded pages, which is honest
 * about itself — a claim about the list in front of the reader. Moving the
 * chips to the server belongs to the management layer (item 7), and this
 * function is where their total would land in turn.
 */
export function counterTotal(
  grammar: CounterGrammar,
  deckCardCount: number,
  shownCount: number,
  foundTotal?: number | null,
): number {
  if (grammar === 'plain') return deckCardCount
  if (grammar === 'found') return foundTotal ?? shownCount
  return shownCount
}

/**
 * Card id → its index in the given list, built once per list.
 *
 * Replaces the per-row `findIndex` that made rendering the feed O(n²) — brief
 * §21.3 item 4 measured 0.9–1.2 s per repaint while searching a large deck,
 * because every row scanned the whole list to discover its own number. Building
 * the map is one pass and each lookup is then constant.
 *
 * Keyed by id rather than by object identity so it survives a refetch replacing
 * card objects with equal-but-new ones.
 */
export function buildCardIndexMap(cards: readonly { id: string }[]): Map<string, number> {
  const map = new Map<string, number>()
  cards.forEach((c, i) => map.set(c.id, i))
  return map
}
